import store from '../store'
import { AddMessageProps, MessageLevel } from '../store/messages'
import { intl } from '../i18n'

// 社外公開デモでは、モック未実装のAPIや想定外の操作に起因するエラー
// Snackbarを見せない。
//
// Snackbar（src/view/components/snackbars/SnackBar/index.tsx のNotifier）は
// state.messages.latest を購読し、その type がWARN/ERRORの時にエラー表示に
// なる。latest を更新するのは messages reducer の以下3アクションのみなので、
// dispatch段階でこの3種のWARN/ERRORを落とす。
//
// 本体側のdispatch元は300箇所以上あり（APIエラー起因のものだけでなく、
// 各ページのhooksがhandleWarning等から直接呼ぶものも含む）、
// MSWのレスポンス整備だけでは塞ぎきれないため、発火条件側で押さえる。
//
// 制約: これはstoreオブジェクトのdispatchプロパティを差し替えるものなので、
// 差し替え前にdispatch関数を捕捉している経路は素通りする。具体的には
// redux-observableのEpicがストリームに流すアクション（createEpicMiddlewareが
// createStore時点のdispatchをsubscribeで捕捉している）は抑止できない。
// 現状メッセージ系アクションを流すEpicは subscribeApiDebugMessageEpic
// （store/messages.ts）だけで、これはFLAGXS_ENV==='development'でしか
// 起動しないため公開デモでは発火しない。Epicから
// addGlobalMessageを流す実装を追加する場合はここでは止まらない点に注意。
// 一方 store/comments.ts のようにcatchError内から store.dispatch() を
// 明示的に呼ぶ経路は、呼び出し時にプロパティを読むため抑止対象になる。
const MESSAGE_ACTION_TYPES = [
  'ADD_GLOBAL_MESSAGE',
  'ADD_SCREEN_MESSAGE',
  'RECEIVED_API_DEBUG_MESSAGE',
]

// SUCCESS/INFOは「保存しました」等のポジティブな通知で、デモの操作感を
// 保つために残す。抑止するのはエラー・警告のみ。
const SUPPRESSED_LEVELS = [MessageLevel.ERROR, MessageLevel.WARN]

// デモ自身が意図して出す通知（「この機能はデモでは使えません」等）は
// WARNだが抑止対象から外す。抑止したいのは本体由来の想定外エラーだけで、
// これらはデモの仕様として出したいもの。表示スタイルはWARNのままにしたい
// ためレベルは変えず、このフラグで区別する。
type DemoIntentionalMessage = AddMessageProps & { demoIntentional?: boolean }

export const demoIntentionalMessage = (
  message: AddMessageProps
): DemoIntentionalMessage => ({ ...message, demoIntentional: true })

// Excel取り込み（ProjectPlanNew/hooks/excel.ts）のバリデーション結果は
// addGlobalMessage(WARN)で通知されるが、これも本体由来の想定外エラーと
// 見分けがつかず一律抑止に巻き込まれると、デモ利用者は不正なExcelを
// 入れても何も表示されず沈黙で失敗する。これはデモでも実際に確認したい
// 操作結果なので、タイトル文言で個別に抑止対象から除外する。
const NON_SUPPRESSED_TITLES = [
  intl.formatMessage({ id: 'projectPlan.import.warning' }),
]

const isSuppressedMessageAction = (action: unknown): boolean => {
  if (typeof action !== 'object' || action === null) {
    return false
  }
  const { type, message } = action as {
    type?: unknown
    message?: { type?: unknown; title?: unknown; demoIntentional?: unknown }
  }
  if (message?.demoIntentional) {
    return false
  }
  if (
    typeof message?.title === 'string' &&
    NON_SUPPRESSED_TITLES.includes(message.title)
  ) {
    return false
  }
  return (
    typeof type === 'string' &&
    MESSAGE_ACTION_TYPES.includes(type) &&
    SUPPRESSED_LEVELS.includes(message?.type as MessageLevel)
  )
}

// storeはredux createStoreの戻り値で、dispatchは書き換え可能な
// プロパティ。この関数はデモモードでのみ動的importされるbrowser.tsから
// 呼ばれるため（src/index.tsx参照。通常ビルドではFLAGXS_DEMO_MODEの
// リテラル置換によりimportごとデッドコード除去される）、本番のstoreには
// 一切影響しない。
export const suppressErrorSnackbar = () => {
  const originalDispatch = store.dispatch
  store.dispatch = ((action: unknown) => {
    if (isSuppressedMessageAction(action)) {
      // 握りつぶす代わりにconsoleには残し、デモ中に起きた問題を開発者が
      // 後から追えるようにする。
      console.warn('[demo mock] suppressed error snackbar', action)
      return action
    }
    return originalDispatch(action as Parameters<typeof originalDispatch>[0])
  }) as typeof store.dispatch
}
